import { useState, useMemo, useEffect, useRef } from "react";
import Icon1 from "@assets/icons/Icon";
import TextField from "@components/ui/textfields/TextField";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { bookingsApi } from "@services/api/bookings";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import { sessionsApi } from "@services/api/sessions";
import VehicleImage from "@components/ui/VehicleImage";
import { useDataStore } from "@store/user_org";

// ─── Types ──────────────────────────────────────────────────

export interface BookingData {
  immatriculation: string;
  service: string;
  intervention_type: string;
  intervention: string;
  custom_intervention: string;
  dont_know_service: boolean;
  deposit_date: string | null;
  deposit_time: string;
  restitution_date: string | null;
  restitution_time: string;
  wait_on_site: boolean | null;
  mobility_option: string;
  phone: string;
  email: string;
  nom: string;
  prenom: string;
  privacy_accepted: boolean;
}

export const INITIAL_BOOKING_DATA: BookingData = {
  immatriculation: "",
  service: "",
  intervention_type: "",
  intervention: "",
  custom_intervention: "",
  dont_know_service: false,
  deposit_date: null,
  deposit_time: "",
  restitution_date: null,
  restitution_time: "",
  wait_on_site: null,
  mobility_option: "",
  phone: "",
  email: "",
  nom: "",
  prenom: "",
  privacy_accepted: false,
};

const SERVICES = [
  { value: "mecanique", label: "Mécanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "vitrage", label: "Vitrage" },
  { value: "pneumatique", label: "Pneumatique" },
];

const MORNING_SLOTS = ["08:00", "08:15", "08:30", "08:45", "09:00", "09:15", "09:30", "09:45", "10:00", "10:15", "10:30", "10:45", "11:00"];
const AFTERNOON_SLOTS = ["14:00", "14:15", "14:30", "14:45", "15:00", "15:15", "15:30", "15:45", "16:00", "16:15", "16:30", "16:45", "17:00", "17:15", "17:30", "17:45", "18:00"];
const ALL_SLOTS = [...MORNING_SLOTS, ...AFTERNOON_SLOTS];

const STEP_TITLES = ["Votre véhicule", "Besoin + créneau", "Date de restitution", "Coordonnées"];
const STEP_COUNT = 4;

// ─── Service icons ───────────────────────────────────────────

function ServiceIcon({ type, size = 26, color = "currentColor" }: { type: string; size?: number; color?: string }) {
  const s: React.CSSProperties = { width: size, height: size, flexShrink: 0 };
  const p = { stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  if (type === "entretien") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>;
  if (type === "reparation") return <svg style={s} viewBox="0 0 24 24" {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
  if (type === "diagnostic") return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
  if (type === "controle_technique") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>;
  if (type === "pneumatique") return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="2" x2="12" y2="9" /><line x1="12" y1="15" x2="12" y2="22" /><line x1="2" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="22" y2="12" /></svg>;
  if (type === "carrosserie") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M6 10l2.5-5h7L18 10" /><rect x="2" y="10" width="20" height="7" rx="2" /><circle cx="7" cy="17" r="2" fill={color} stroke="none" /><circle cx="17" cy="17" r="2" fill={color} stroke="none" /><line x1="12" y1="5" x2="12" y2="10" /></svg>;
  return <span style={s} />;
}

// ─── Step indicator ─────────────────────────────────────────

function StepIndicator({ step }: { step: number }) {
  return (
    <div className="flex flex-row items-center gap-[5px]">
      {Array.from({ length: STEP_COUNT }).map((_, i) => (
        <div key={i} className="h-1 flex-1 rounded-[5px]" style={{ background: i <= step ? "#000" : "#E5E5E5" }} />
      ))}
    </div>
  );
}

// ─── Calendar ───────────────────────────────────────────────

const DAYS_HEADER = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

function MiniCalendar({
  selectedDate, onSelect, minDate, maxDate, fullDays, onMonthChange,
}: {
  selectedDate: string | null;
  onSelect: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  fullDays?: string[] | null;
  onMonthChange?: (monthStr: string) => void;
}) {
  const [viewMonth, setViewMonth] = useState(() => {
    const ref = selectedDate || minDate;
    if (ref) { const d = new Date(ref + "T12:00:00"); return { year: d.getFullYear(), month: d.getMonth() }; }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  useEffect(() => {
    onMonthChange?.(`${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeMonth = (next: { year: number; month: number }) => {
    setViewMonth(next);
    onMonthChange?.(`${next.year}-${String(next.month + 1).padStart(2, "0")}`);
  };

  const monthLabel = useMemo(() => {
    const d = new Date(viewMonth.year, viewMonth.month, 1);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [viewMonth]);

  const weeks = useMemo(() => {
    const first = new Date(viewMonth.year, viewMonth.month, 1);
    const lastDay = new Date(viewMonth.year, viewMonth.month + 1, 0).getDate();
    let startDow = first.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;
    const cells: { day: number; inMonth: boolean; dateStr: string }[] = [];
    const prevMonthLast = new Date(viewMonth.year, viewMonth.month, 0).getDate();
    for (let i = startDow - 1; i >= 0; i--) {
      const d = prevMonthLast - i;
      const m = viewMonth.month === 0 ? 11 : viewMonth.month - 1;
      const y = viewMonth.month === 0 ? viewMonth.year - 1 : viewMonth.year;
      cells.push({ day: d, inMonth: false, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    for (let d = 1; d <= lastDay; d++) {
      cells.push({ day: d, inMonth: true, dateStr: `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        const m = viewMonth.month === 11 ? 0 : viewMonth.month + 1;
        const y = viewMonth.month === 11 ? viewMonth.year + 1 : viewMonth.year;
        cells.push({ day: d, inMonth: false, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
      }
    }
    const rows: typeof cells[] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [viewMonth]);

  const cutoff = minDate || new Date().toISOString().slice(0, 10);
  const loadingDays = fullDays === null;
  const fullSet = useMemo(() => new Set(fullDays || []), [fullDays]);
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex flex-row items-center justify-between">
        <button className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-neutral-100 transition-colors"
          onClick={() => changeMonth(viewMonth.month === 0 ? { year: viewMonth.year - 1, month: 11 } : { year: viewMonth.year, month: viewMonth.month - 1 })}>
          <svg width={7} height={12}><path d="M6 1 L1 6 L6 11" fill="none" stroke="#9CA3AF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#374151", textTransform: "capitalize" }}>{monthLabel}</span>
        <button className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-neutral-100 transition-colors"
          onClick={() => changeMonth(viewMonth.month === 11 ? { year: viewMonth.year + 1, month: 0 } : { year: viewMonth.year, month: viewMonth.month + 1 })}>
          <svg width={7} height={12}><path d="M1 1 L6 6 L1 11" fill="none" stroke="#9CA3AF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <div className="grid grid-cols-7">
        {DAYS_HEADER.map((d) => (
          <span key={d} className="flex items-center justify-center h-7" style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#9CA3AF" }}>{d}</span>
        ))}
      </div>
      <div className="flex flex-col">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7">
            {week.map((cell) => {
              const isSelected = selectedDate === cell.dateStr;
              const isToday = cell.dateStr === todayStr && cell.inMonth;
              const dow = new Date(cell.dateStr + "T12:00:00").getDay();
              const isWeekend = dow === 0 || dow === 6;
              const beyondMax = maxDate ? cell.dateStr > maxDate : false;
              const full = cell.inMonth && cell.dateStr >= cutoff && fullSet.has(cell.dateStr);
              const disabled = !cell.inMonth || cell.dateStr < cutoff || beyondMax || full || loadingDays || isWeekend;
              return (
                <button key={cell.dateStr}
                  className={`flex items-center justify-center mx-auto rounded-full transition-colors ${!disabled && !isSelected ? "hover:bg-neutral-100" : ""}`}
                  style={{
                    width: 34, height: 34,
                    fontFamily: "Poppins", fontWeight: isSelected || isToday ? 600 : 400, fontSize: 13,
                    color: isSelected ? "#fff" : disabled ? "#D4D4D4" : isToday ? "#000" : "#4B5563",
                    background: isSelected ? "#000" : "transparent",
                    textDecoration: full ? "line-through" : "none",
                    cursor: disabled ? "default" : "pointer",
                    border: isToday && !isSelected ? "1.5px solid #D1D5DB" : "1.5px solid transparent",
                  }}
                  onClick={() => !disabled && onSelect(cell.dateStr)}
                  disabled={disabled}
                >{cell.day}</button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Need cards ──────────────────────────────────────────────

export const NEED_CARDS = [
  { id: "entretien",          iconType: "entretien",         label: "Entretien",              service: "mecanique",   type: "entretien" },
  { id: "reparation",         iconType: "reparation",        label: "Réparation",             service: "mecanique",   type: "reparation" },
  { id: "diagnostic",         iconType: "diagnostic",        label: "Diagnostic",             service: "mecanique",   type: "diagnostic" },
  { id: "controle_technique", iconType: "controle_technique", label: "Contrôle\ntechnique",   service: "mecanique",   type: "controle_technique" },
  { id: "pneumatique",        iconType: "pneumatique",       label: "Pneus",                  service: "pneumatique", type: "entretien" },
  { id: "carrosserie",        iconType: "carrosserie",       label: "Carrosserie",            service: "carrosserie", type: "reparation" },
];

// ─── Step 0 — Plate (one field, like Midas) ──────────────────

function StepPlate({
  data, update, orgId,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
}) {
  const [lookup, setLookup] = useState<{ found: boolean; marque?: string; modele?: string; coloris?: string; mec?: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const plate = data.immatriculation.replace(/[\s-]/g, "");
    if (plate.length < 7) { setLookup(null); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await bookingsApi.lookupVehicle(data.immatriculation, orgId);
        setLookup(result);
      } catch { setLookup(null); }
      setLoading(false);
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.immatriculation, orgId]);

  return (
    <div className="flex flex-col items-center gap-5 py-4">
      <div className="flex flex-col items-center gap-1">
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 18, color: "#000" }}>
          Quelle est votre plaque ?
        </span>
        <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>
          Nous identifions votre véhicule automatiquement
        </span>
      </div>

      {/* Big plate input */}
      <div className="w-full">
        <input
          type="text"
          value={data.immatriculation}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 7);
            let result = "";
            for (let i = 0; i < raw.length; i++) {
              if (i === 2 || i === 5) result += "-";
              result += raw[i];
            }
            update("immatriculation", result);
          }}
          placeholder="AA-123-BB"
          className="w-full text-center"
          style={{
            fontFamily: "Poppins", fontWeight: 600, fontSize: 24, letterSpacing: 2,
            color: "#000", border: "2px solid #E5E5E5", borderRadius: 14,
            padding: "16px 20px", outline: "none", background: "#FAFAFA",
          }}
          autoFocus
        />
      </div>

      {/* Vehicle result */}
      <div style={{ minHeight: 80 }} className="flex items-center justify-center w-full">
        {loading && (
          <div className="flex items-center gap-2">
            <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid #E5E5E5", borderTopColor: "#000", animation: "spin 0.6s linear infinite" }} />
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Identification...</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {!loading && lookup?.found && (
          <div className="flex flex-row items-center gap-3 w-full rounded-xl p-3" style={{ background: "#F5F5F5", border: "1px solid #E5E5E5" }}>
            <div style={{ height: 48, flexShrink: 0 }} className="flex items-center">
              <VehicleImage marque={lookup.marque} modele={lookup.modele} coloris={lookup.coloris} mec={lookup.mec} className="h-[48px]" iconSize={24} />
            </div>
            <div className="flex flex-col">
              <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "#000" }}>
                {lookup.marque} {lookup.modele}
              </span>
              <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>
                {data.immatriculation}
              </span>
            </div>
          </div>
        )}
        {!loading && lookup && !lookup.found && data.immatriculation.replace(/[\s-]/g, "").length >= 7 && (
          <div className="flex items-center gap-2 w-full rounded-xl p-3" style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}>
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#EA580C" }}>
              Véhicule non reconnu — vous pouvez continuer
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 1 — Service + Date + Time (single screen) ──────────

export function StepServiceDate({
  data, update, orgId, minDaysAhead, maxDaysAhead, isFast, srMinHoursAhead, interventions, estimatedMinutes, onEstimatedReadyTimes, internalMode, isManager, onForceSlot,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
  minDaysAhead: number;
  maxDaysAhead: number;
  isFast: boolean;
  srMinHoursAhead: number;
  interventions: Intervention[];
  estimatedMinutes: number;
  onEstimatedReadyTimes?: (times: Record<string, string>) => void;
  internalMode?: boolean;
  isManager?: boolean;
  onForceSlot?: (forced: boolean) => void;
}) {
  const selectedCard = NEED_CARDS.find((c) => c.id === data.intervention_type);

  const matchingInterventions = useMemo(() => {
    if (!selectedCard) return [];
    return interventions.filter((i) => i.type === selectedCard.id && i.active);
  }, [selectedCard, interventions]);

  const selectCard = (card: typeof NEED_CARDS[0]) => {
    update("service", card.service);
    update("intervention_type", card.id);
    const firstMatch = interventions.find((i) => i.type === card.id && i.active);
    update("intervention", firstMatch?.name || "");
    update("custom_intervention", "");
    update("dont_know_service", false);
  };

  const isAutre = data.intervention === "__autre__";

  const toggleDontKnow = () => {
    const next = !data.dont_know_service;
    update("dont_know_service", next);
    if (next) { update("service", ""); update("intervention_type", ""); update("intervention", ""); update("custom_intervention", ""); }
  };

  // ─── Date logic ──────────────────────────────────────────
  const [fullDays, setFullDays] = useState<string[] | null>(null);
  const [unavailable, setUnavailable] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [noAvailability, setNoAvailability] = useState(false);
  const autoSelectAttemptsRef = useRef(0);

  const { minDate, cutoffSlots } = useMemo(() => {
    if (isFast) {
      const now = new Date();
      const cutoff = new Date(now.getTime() + srMinHoursAhead * 60 * 60 * 1000);
      const cutoffDate = cutoff.toISOString().slice(0, 10);
      const cutoffMinutes = cutoff.getHours() * 60 + cutoff.getMinutes();
      const disabled = ALL_SLOTS.filter((s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m < cutoffMinutes; });
      return { minDate: cutoffDate, cutoffSlots: disabled };
    }
    const d = new Date(); d.setDate(d.getDate() + minDaysAhead);
    return { minDate: d.toISOString().slice(0, 10), cutoffSlots: [] as string[] };
  }, [minDaysAhead, isFast, srMinHoursAhead]);

  const maxDate = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + maxDaysAhead);
    return d.toISOString().slice(0, 10);
  }, [maxDaysAhead]);

  const currentMonthStr = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }, []);
  const viewedMonthRef = useRef(currentMonthStr);
  const fetchFullDays = (month: string) => {
    if (!orgId) return;
    viewedMonthRef.current = month;
    bookingsApi.getFullDays(month, orgId, isFast || undefined, data.service || undefined, estimatedMinutes || undefined).then((res) => setFullDays((prev) => [...(prev || []).filter((d) => !d.startsWith(month)), ...res.full_days])).catch(() => setFullDays((prev) => prev ?? []));
  };
  useEffect(() => {
    const viewedMonth = viewedMonthRef.current;
    setFullDays(null); update("deposit_date", ""); update("deposit_time", "");
    setNoAvailability(false);
    autoSelectAttemptsRef.current = 0;
    fetchFullDays(currentMonthStr);
    if (viewedMonth !== currentMonthStr) fetchFullDays(viewedMonth);
  }, [orgId, isFast, data.service, estimatedMinutes]);

  // Auto-select first available date
  useEffect(() => {
    if (data.deposit_date || fullDays === null || noAvailability) return;
    const fullSet = new Set(fullDays);
    const d = new Date(minDate + "T12:00:00");
    const max = new Date(maxDate + "T12:00:00");
    let found = false;
    for (let i = 0; i < 120; i++) {
      if (d > max) break;
      if (d.getDay() !== 0 && d.getDay() !== 6 && !fullSet.has(d.toISOString().slice(0, 10))) { found = true; break; }
      d.setDate(d.getDate() + 1);
    }
    if (!found) {
      setNoAvailability(true);
      return;
    }
    update("deposit_date", d.toISOString().slice(0, 10));
  }, [fullDays, data.deposit_date, noAvailability]);

  // Fetch availability
  useEffect(() => {
    if (!data.deposit_date || !orgId) { setUnavailable([]); return; }
    setLoadingSlots(true);
    const selectedDate = data.deposit_date;
    bookingsApi.getAvailability(selectedDate, data.service || "Mécanique", orgId, isFast || undefined, estimatedMinutes || undefined)
      .then((res) => {
        setUnavailable(res.unavailable_slots);
        onEstimatedReadyTimes?.(res.estimated_ready_times || {});
        // If every displayable slot is unavailable, mark as full and move on
        const unavailSet = new Set(res.unavailable_slots);
        const anyAvailable = ALL_SLOTS.some((s) => !unavailSet.has(s));
        if (!anyAvailable && !isManager) {
          autoSelectAttemptsRef.current += 1;
          // After 5 failed auto-selects, stop trying — no availability
          if (autoSelectAttemptsRef.current >= 5) {
            setNoAvailability(true);
            update("deposit_date", "");
            update("deposit_time", "");
            return;
          }
          setFullDays((prev) => {
            if (!prev) return prev;                       // still loading full-days, skip
            if (prev.includes(selectedDate)) return prev; // already known
            return [...prev, selectedDate];
          });
          update("deposit_date", "");
          update("deposit_time", "");
        } else if (!anyAvailable && isManager) {
          // Manager can force — keep the date, flag as forced
          onForceSlot?.(true);
        }
      })
      .catch(() => setUnavailable([]))
      .finally(() => setLoadingSlots(false));
  }, [data.deposit_date, data.service, orgId, isFast]);

  // Filter available slots (hide unavailable ones, or show them for managers)
  const unavailableSet = useMemo(() => {
    const allUnavailable = isFast && data.deposit_date === minDate
      ? Array.from(new Set([...unavailable, ...cutoffSlots]))
      : unavailable;
    return new Set(allUnavailable);
  }, [unavailable, cutoffSlots, isFast, data.deposit_date, minDate]);

  const availableSlots = useMemo(() => {
    if (isManager) return ALL_SLOTS; // Managers see all slots
    return ALL_SLOTS.filter((s) => !unavailableSet.has(s));
  }, [unavailableSet, isManager]);

  const availableMorning = useMemo(() => MORNING_SLOTS.filter((s) => availableSlots.includes(s)), [availableSlots]);
  const availableAfternoon = useMemo(() => AFTERNOON_SLOTS.filter((s) => availableSlots.includes(s)), [availableSlots]);

  // Auto-select first available time slot
  useEffect(() => {
    if (!data.deposit_date || loadingSlots) return;
    if (data.deposit_time && availableSlots.includes(data.deposit_time)) return;
    const first = availableSlots[0];
    if (first) update("deposit_time", first);
  }, [availableSlots, loadingSlots, data.deposit_date]);

  const showDatePicker = data.service || data.dont_know_service;

  return (
    <div className="flex flex-col gap-4">
      {/* Service cards */}
      <div className="flex flex-col gap-2">
        {!data.dont_know_service && (
          <div className="grid grid-cols-3 gap-1.5">
            {NEED_CARDS.map((card) => {
              const isSelected = selectedCard?.id === card.id;
              return (
                <button key={card.id} onClick={() => selectCard(card)}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 px-1 rounded-xl transition-all"
                  style={{
                    border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                    background: isSelected ? "#F5F5F5" : "#fff",
                    cursor: "pointer",
                  }}>
                  <ServiceIcon type={card.iconType} size={22} color={isSelected ? "#000" : "#999"} />
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 10, color: isSelected ? "#000" : "#666", textAlign: "center", whiteSpace: "pre-line", lineHeight: "13px" }}>
                    {card.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <button onClick={toggleDontKnow}
          className="flex items-center gap-2 py-1"
          style={{ background: "none", border: "none", cursor: "pointer" }}>
          <span style={{ fontFamily: "Poppins", fontSize: 12, color: data.dont_know_service ? "#000" : "#A1A1A1", textDecoration: "underline" }}>
            {data.dont_know_service ? "Choisir un service" : "Je ne sais pas encore"}
          </span>
        </button>
      </div>

      {/* Intervention picker */}
      {selectedCard && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-1.5">
            {matchingInterventions.map((intv) => {
              const isSelected = data.intervention === intv.name;
              return (
                <button key={intv.id} onClick={() => { update("intervention", isSelected ? "" : intv.name); update("custom_intervention", ""); }}
                  className="px-3 py-1.5 rounded-lg transition-all"
                  style={{
                    fontFamily: "Poppins", fontSize: 11, fontWeight: 500,
                    border: isSelected ? "1.5px solid #000" : "1.5px solid #E5E5E5",
                    background: isSelected ? "#F5F5F5" : "#fff",
                    color: isSelected ? "#000" : "#666",
                    cursor: "pointer",
                  }}>
                  {intv.name}
                </button>
              );
            })}
            <button onClick={() => { update("intervention", isAutre ? "" : "__autre__"); if (!isAutre) update("custom_intervention", ""); }}
              className="px-3 py-1.5 rounded-lg transition-all"
              style={{
                fontFamily: "Poppins", fontSize: 11, fontWeight: 500,
                border: isAutre ? "1.5px solid #000" : "1.5px solid #E5E5E5",
                background: isAutre ? "#F5F5F5" : "#fff",
                color: isAutre ? "#000" : "#666",
                cursor: "pointer",
              }}>
              Autre
            </button>
          </div>
          {isAutre && (
            <textarea
              value={data.custom_intervention}
              onChange={(e) => update("custom_intervention", e.target.value)}
              placeholder="Décrivez votre besoin..."
              rows={3}
              style={{
                fontFamily: "Poppins", fontSize: 12, color: "#333",
                border: "1.5px solid #E5E5E5", borderRadius: 10, padding: "10px 12px",
                outline: "none", resize: "none", background: "#FAFAFA",
              }}
            />
          )}
        </div>
      )}

      {/* Calendar + Time — inline, Doctolib style */}
      {showDatePicker && noAvailability && !isManager && (
        <div className="flex flex-col items-center gap-2 py-6 px-4 rounded-xl" style={{ background: "#FFF7ED", border: "1.5px solid #FED7AA" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#9A3412" }}>
            Aucun créneau disponible
          </span>
          <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#C2410C", textAlign: "center", lineHeight: "18px" }}>
            Il n'y a actuellement aucun créneau disponible pour ce service. Veuillez nous contacter directement par téléphone.
          </span>
        </div>
      )}
      {showDatePicker && (noAvailability && isManager) && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "#FEF3C7", border: "1px solid #F59E0B" }}>
            <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#92400E" }}>
              Aucun créneau disponible. En tant que manager, vous pouvez forcer un créneau.
            </span>
          </div>
          <MiniCalendar
            selectedDate={data.deposit_date}
            onSelect={(d) => { update("deposit_date", d); update("deposit_time", ""); onForceSlot?.(true); }}
            minDate={minDate}
            maxDate={maxDate}
            fullDays={[]}
            onMonthChange={fetchFullDays}
          />
          {data.deposit_date && !loadingSlots && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#A1A1A1" }}>Matin</span>
                <div className="flex flex-wrap gap-1">
                  {MORNING_SLOTS.map((slot) => {
                    const isSelected = data.deposit_time === slot;
                    return (
                      <button key={slot} onClick={() => { update("deposit_time", slot); onForceSlot?.(true); }}
                        className="flex items-center justify-center py-1.5 px-2.5 rounded-lg transition-all"
                        style={{
                          fontFamily: "Poppins", fontWeight: 500, fontSize: 12,
                          border: isSelected ? "2px solid #DC2626" : "1.5px dashed #FBBF24",
                          background: isSelected ? "#DC2626" : "#FFFBEB",
                          color: isSelected ? "#fff" : "#92400E",
                          cursor: "pointer",
                        }}>
                        {slot.replace(":00", "h").replace(":15", "h15").replace(":30", "h30").replace(":45", "h45")}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#A1A1A1" }}>Après-midi</span>
                <div className="flex flex-wrap gap-1">
                  {AFTERNOON_SLOTS.map((slot) => {
                    const isSelected = data.deposit_time === slot;
                    return (
                      <button key={slot} onClick={() => { update("deposit_time", slot); onForceSlot?.(true); }}
                        className="flex items-center justify-center py-1.5 px-2.5 rounded-lg transition-all"
                        style={{
                          fontFamily: "Poppins", fontWeight: 500, fontSize: 12,
                          border: isSelected ? "2px solid #DC2626" : "1.5px dashed #FBBF24",
                          background: isSelected ? "#DC2626" : "#FFFBEB",
                          color: isSelected ? "#fff" : "#92400E",
                          cursor: "pointer",
                        }}>
                        {slot.replace(":00", "h").replace(":15", "h15").replace(":30", "h30").replace(":45", "h45")}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {data.deposit_date && loadingSlots && (
            <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>Chargement...</span>
          )}
        </div>
      )}
      {showDatePicker && !noAvailability && (
        <div className="flex flex-col gap-3">
          {isManager && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "#F5F5F5", border: "1px solid #E5E5E5" }}>
              <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#666" }}>
                Les créneaux en jaune sont complets. Vous pouvez les forcer.
              </span>
            </div>
          )}
          <MiniCalendar
            selectedDate={data.deposit_date}
            onSelect={(d) => { update("deposit_date", d); update("deposit_time", ""); if (!isManager) onForceSlot?.(false); }}
            minDate={minDate}
            maxDate={maxDate}
            fullDays={isManager ? [] : fullDays}
            onMonthChange={fetchFullDays}
          />
          {data.deposit_date && !loadingSlots && (
            <div className="flex flex-col gap-2">
              {availableMorning.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#A1A1A1" }}>Matin</span>
                  <div className="flex flex-wrap gap-1">
                    {availableMorning.map((slot) => {
                      const isSelected = data.deposit_time === slot;
                      const isFull = !!isManager && unavailableSet.has(slot);
                      return (
                        <button key={slot} onClick={() => { update("deposit_time", slot); onForceSlot?.(isFull); }}
                          className="flex items-center justify-center py-1.5 px-2.5 rounded-lg transition-all relative"
                          style={{
                            fontFamily: "Poppins", fontWeight: 500, fontSize: 12,
                            border: isSelected ? (isFull ? "2px solid #DC2626" : "2px solid #000") : isFull ? "1.5px dashed #FBBF24" : "1.5px solid #E5E5E5",
                            background: isSelected ? (isFull ? "#DC2626" : "#000") : isFull ? "#FFFBEB" : "#fff",
                            color: isSelected ? "#fff" : isFull ? "#92400E" : "#333",
                            cursor: "pointer",
                          }}>
                          {slot.replace(":00", "h").replace(":15", "h15").replace(":30", "h30").replace(":45", "h45")}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {availableAfternoon.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#A1A1A1" }}>Après-midi</span>
                  <div className="flex flex-wrap gap-1">
                    {availableAfternoon.map((slot) => {
                      const isSelected = data.deposit_time === slot;
                      const isFull = !!isManager && unavailableSet.has(slot);
                      return (
                        <button key={slot} onClick={() => { update("deposit_time", slot); onForceSlot?.(isFull); }}
                          className="flex items-center justify-center py-1.5 px-2.5 rounded-lg transition-all relative"
                          style={{
                            fontFamily: "Poppins", fontWeight: 500, fontSize: 12,
                            border: isSelected ? (isFull ? "2px solid #DC2626" : "2px solid #000") : isFull ? "1.5px dashed #FBBF24" : "1.5px solid #E5E5E5",
                            background: isSelected ? (isFull ? "#DC2626" : "#000") : isFull ? "#FFFBEB" : "#fff",
                            color: isSelected ? "#fff" : isFull ? "#92400E" : "#333",
                            cursor: "pointer",
                          }}>
                          {slot.replace(":00", "h").replace(":15", "h15").replace(":30", "h30").replace(":45", "h45")}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {availableMorning.length === 0 && availableAfternoon.length === 0 && (
                <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>Aucun créneau disponible pour cette date</span>
              )}
            </div>
          )}
          {data.deposit_date && loadingSlots && (
            <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>Chargement...</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mobility picker ──────────────────────────────────────────

function MobilityPicker({ selected, onSelect }: { selected: string; onSelect: (key: string) => void }) {
  return (
    <div className="flex flex-col gap-2">
      <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#333" }}>
        Solution de mobilité
      </span>
      <div className="flex flex-col gap-1.5">
        {MOBILITY_OPTIONS.map((opt) => {
          const isSelected = selected === opt.key;
          return (
            <button key={opt.key} onClick={() => onSelect(opt.key)}
              className="flex flex-row items-center gap-3 py-2.5 px-3 rounded-xl transition-all"
              style={{
                fontFamily: "Poppins",
                border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                background: isSelected ? "#F5F5F5" : "#fff",
                cursor: "pointer",
              }}>
              <span style={{ fontWeight: 500, fontSize: 13, color: isSelected ? "#000" : "#444" }}>{opt.label}</span>
              {opt.sub && <span style={{ fontSize: 11, color: "#999" }}>{opt.sub}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 2 — Restitution ─────────────────────────────────────

const MOBILITY_OPTIONS = [
  { key: "velo", label: "Vélo électrique", sub: "Gratuit" },
  { key: "pret", label: "Véhicule de prêt", sub: "Zone limitée" },
  { key: "location", label: "Véhicule de location", sub: "Liberté / payant" },
  { key: "aucun", label: "Aucun", sub: "" },
];

const SHORT_THRESHOLD = 150; // 2h30 in minutes

function formatEstimatedDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < SHORT_THRESHOLD) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  }
  const days = Math.ceil(minutes / 480);
  return `${days} jour${days > 1 ? "s" : ""}`;
}

function computeRestitutionTime(depositTime: string, totalMinutes: number): string {
  const [h, m] = depositTime.split(":").map(Number);
  const total = h * 60 + m + totalMinutes;
  const rh = Math.floor(total / 60);
  const rm = total % 60;
  if (rh >= 18) return "18:00";
  // Round up to next 15-min slot
  const rounded = Math.ceil(rm / 15) * 15;
  const fh = rounded >= 60 ? Math.min(rh + 1, 18) : rh;
  const fm = rounded >= 60 ? 0 : rounded;
  return `${String(fh).padStart(2, "0")}:${String(fm).padStart(2, "0")}`;
}

function addBusinessDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T12:00:00");
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return d.toISOString().slice(0, 10);
}

function StepRestitution({
  data, update, estimatedMinutes, safetyMarginHours, depositDate, depositTime, estimatedReadyTimes,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  estimatedMinutes: number;
  safetyMarginHours: number;
  depositDate: string | null;
  depositTime: string;
  estimatedReadyTimes?: Record<string, string>;
}) {
  const isShort = estimatedMinutes > 0 && estimatedMinutes < SHORT_THRESHOLD;
  const totalMinutes = estimatedMinutes + safetyMarginHours * 60;

  // Use server-computed ready time if available, otherwise fall back to naive computation
  const getReadyTime = (time: string) => {
    if (estimatedReadyTimes && estimatedReadyTimes[time]) return estimatedReadyTimes[time];
    return computeRestitutionTime(time, totalMinutes);
  };

  // Auto-compute restitution for "wait on site"
  useEffect(() => {
    if (!isShort || data.wait_on_site !== true || !depositDate || !depositTime) return;
    const restTime = getReadyTime(depositTime);
    update("restitution_date", depositDate);
    update("restitution_time", restTime);
    update("mobility_option", "attente");
  }, [data.wait_on_site, depositDate, depositTime, isShort, totalMinutes, estimatedReadyTimes]);

  // Auto-compute restitution for long interventions + default mobility
  useEffect(() => {
    if (isShort || !depositDate) return;
    if (estimatedMinutes > 0) {
      const businessDays = Math.max(1, Math.ceil(estimatedMinutes / 480));
      update("restitution_date", addBusinessDays(depositDate, businessDays));
      update("restitution_time", "18:00");
    }
    if (!data.mobility_option) update("mobility_option", "location");
  }, [depositDate, isShort, estimatedMinutes]);

  // Compute earliest pickup time for short + not waiting
  const earliestPickup = useMemo(() => {
    if (!depositTime) return "18:00";
    return getReadyTime(depositTime);
  }, [depositTime, totalMinutes, estimatedReadyTimes]);

  const pickupSlots = useMemo(() => {
    return ALL_SLOTS.filter((s) => s >= earliestPickup);
  }, [earliestPickup]);

  // Auto-set restitution when user picks "not waiting" + default mobility
  useEffect(() => {
    if (!isShort || data.wait_on_site !== false || !depositDate) return;
    update("restitution_date", depositDate);
    if (!data.mobility_option || data.mobility_option === "attente") update("mobility_option", "velo");
    if (!data.restitution_time || data.restitution_time < earliestPickup) {
      update("restitution_time", pickupSlots[0] || "18:00");
    }
  }, [data.wait_on_site, data.mobility_option, depositDate, isShort, earliestPickup]);

  const durationLabel = formatEstimatedDuration(estimatedMinutes);

  return (
    <div className="flex flex-col gap-4">
      {/* Duration info */}
      <div className="flex flex-col items-center gap-1 pb-1">
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 15, color: "#000" }}>
          {isShort ? "Restitution" : "Restitution"}
        </span>
        <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1", textAlign: "center" }}>
          {estimatedMinutes > 0
            ? `Cette intervention prend environ ${durationLabel}`
            : "Choisissez une date de récupération souhaitée"}
        </span>
      </div>

      {/* SHORT: wait on site question */}
      {isShort && (
        <div className="flex flex-col gap-2">
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#333" }}>
            Souhaitez-vous attendre sur place ?
          </span>
          <div className="grid grid-cols-2 gap-2">
            {([true, false] as const).map((val) => {
              const selected = data.wait_on_site === val;
              return (
                <button key={String(val)} onClick={() => { update("wait_on_site", val); if (val) update("mobility_option", "attente"); }}
                  className="flex items-center justify-center py-2.5 rounded-xl transition-all"
                  style={{
                    fontFamily: "Poppins", fontWeight: 500, fontSize: 13,
                    border: selected ? "2px solid #000" : "1.5px solid #E5E5E5",
                    background: selected ? "#F5F5F5" : "#fff",
                    color: selected ? "#000" : "#666",
                    cursor: "pointer",
                  }}>
                  {val ? "Oui, j'attends" : "Non, je repasse"}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* SHORT + WAIT: confirmation */}
      {isShort && data.wait_on_site === true && depositDate && (
        <div className="flex flex-col items-center gap-1 py-2 px-3 rounded-xl" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#166534" }}>
            Votre véhicule sera prêt vers {getReadyTime(depositTime).replace(":", "h")}
          </span>
        </div>
      )}

      {/* SHORT + NOT WAITING: mobility + time picker */}
      {isShort && data.wait_on_site === false && (
        <>
          <MobilityPicker selected={data.mobility_option} onSelect={(k) => update("mobility_option", k)} />
          {data.mobility_option && (
            <div className="flex flex-col gap-1">
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#A1A1A1" }}>
                À quelle heure récupérer votre véhicule ? (à partir de {earliestPickup.replace(":", "h")})
              </span>
              <div className="flex flex-wrap gap-1">
                {pickupSlots.map((slot) => {
                  const isSelected = data.restitution_time === slot;
                  return (
                    <button key={slot} onClick={() => update("restitution_time", slot)}
                      className="flex items-center justify-center py-1.5 px-2.5 rounded-lg transition-all"
                      style={{
                        fontFamily: "Poppins", fontWeight: 500, fontSize: 12,
                        border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                        background: isSelected ? "#000" : "#fff",
                        color: isSelected ? "#fff" : "#333",
                        cursor: "pointer",
                      }}>
                      {slot.replace(":00", "h").replace(":15", "h15").replace(":30", "h30").replace(":45", "h45")}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* LONG / UNKNOWN: mobility + restitution date picker */}
      {!isShort && (
        <>
          <MobilityPicker selected={data.mobility_option} onSelect={(k) => update("mobility_option", k)} />
          {estimatedMinutes > 0 ? (
            <div className="flex flex-col items-center gap-1 py-2 px-3 rounded-xl" style={{ background: "#F5F5F5", border: "1px solid #E5E5E5" }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#666", textAlign: "center" }}>
                La date de restitution vous sera communiquée par votre garage
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#333" }}>
                Date de récupération souhaitée
              </span>
              <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#A1A1A1" }}>
                Minimum 2 jours ouvrables après le dépôt
              </span>
              {depositDate && (
                <>
                  <MiniCalendar
                    selectedDate={data.restitution_date}
                    onSelect={(d) => { update("restitution_date", d); update("restitution_time", ""); }}
                    minDate={addBusinessDays(depositDate, 2)}
                  />
                  {data.restitution_date && (
                    <div className="flex flex-col gap-1">
                      <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#A1A1A1" }}>Heure de récupération</span>
                      <div className="flex flex-wrap gap-1">
                        {ALL_SLOTS.map((slot) => {
                          const isSelected = data.restitution_time === slot;
                          return (
                            <button key={slot} onClick={() => update("restitution_time", slot)}
                              className="flex items-center justify-center py-1.5 px-2.5 rounded-lg transition-all"
                              style={{
                                fontFamily: "Poppins", fontWeight: 500, fontSize: 12,
                                border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                                background: isSelected ? "#000" : "#fff",
                                color: isSelected ? "#fff" : "#333",
                                cursor: "pointer",
                              }}>
                              {slot.replace(":00", "h").replace(":15", "h15").replace(":30", "h30").replace(":45", "h45")}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Step 3 — Contact (3 fields + checkbox) ──────────────────

function StepContact({
  data, update, orgId, internalMode,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
  internalMode?: boolean;
}) {
  const [clientLookup, setClientLookup] = useState<{ found: boolean; nom?: string; prenom?: string } | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const phone = data.phone.replace(/\s/g, "");
    if (phone.length < 10) { setClientLookup(null); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const result = await bookingsApi.lookupClient(data.phone, orgId);
        setClientLookup(result);
        if (result.found) {
          if (result.nom) update("nom", result.nom);
          if (result.prenom) update("prenom", result.prenom);
          if (result.email) update("email", result.email);
        }
      } catch { setClientLookup(null); }
      setLookingUp(false);
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.phone, orgId]);

  const recognized = clientLookup?.found === true;

  return (
    <form className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()} autoComplete="on">
      <div className="flex flex-col items-center gap-1 pb-1">
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#000" }}>
          {internalMode ? "Coordonnées du client" : "Dernière étape"}
        </span>
        <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>
          {internalMode ? "Renseignez les informations du client" : "Nous vous contacterons pour confirmer votre RDV"}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <TextField label="Téléphone" value={data.phone} onChange={(v) => update("phone", v.replace(/^\+33\s?/, "0"))} placeholder="06 12 34 56 78" type="phone" autoComplete="tel" name="tel" />
        {lookingUp && <span className="text-hx text-neutral-400 pl-1">Recherche...</span>}
        {recognized && (
          <div className="flex items-center px-3 py-2 rounded-lg" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
            <span style={{ fontSize: 13, color: "#166534", fontFamily: "Poppins" }}>Rebonjour ! Vos infos ont été pré-remplies.</span>
          </div>
        )}
      </div>

      {!recognized && (
        <>
          <TextField label="Email" value={data.email} onChange={(v) => update("email", v)} placeholder="votre@email.com" type="email" autoComplete="email" name="email" />
          <div className="flex flex-col gap-1">
            <TextField label="Nom" value={data.nom} onChange={(v) => update("nom", v)} autoComplete="family-name" name="family-name" />
            <TextField label="Prénom" value={data.prenom} onChange={(v) => update("prenom", v)} autoComplete="given-name" name="given-name" />
          </div>
        </>
      )}

      {!internalMode && (
        <CheckBoxInput
          checked={data.privacy_accepted}
          label="J'accepte la politique de confidentialité"
          onChange={() => update("privacy_accepted", !data.privacy_accepted)}
        />
      )}
    </form>
  );
}

// ─── Confirmation ────────────────────────────────────────────

function ConfirmationScreen({ data, onClose, bookingId, trackingUrl, whatsappPhone }: {
  data: BookingData; onClose: () => void; bookingId: string; trackingUrl: string; whatsappPhone: string;
}) {
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const formatDate = (d: string | null) => {
    if (!d) return "--";
    const parts = d.split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };
  const ref = bookingId ? `#${bookingId.slice(0, 8).toUpperCase()}` : "";
  const depositLabel = data.deposit_date ? `${formatDate(data.deposit_date)} à ${data.deposit_time}` : "";
  const waMessage = `Bonjour, je confirme mon RDV ${ref} du ${depositLabel} pour mon véhicule ${data.immatriculation || ""}. Merci !`;
  const waLink = whatsappPhone
    ? `https://wa.me/${whatsappPhone.replace(/[^0-9+]/g, "")}?text=${encodeURIComponent(waMessage)}`
    : null;
  const hasEmail = !!data.email;

  return (
    <div className="flex flex-col items-center gap-5 p-5">
      <div className="flex justify-end w-full">
        <button onClick={onClose} className="bg-transparent border-none cursor-pointer p-1">
          <Icon1 name="close" size={12} color="#A1A1A1" />
        </button>
      </div>

      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center justify-center w-14 h-14 rounded-full" style={{ background: "#F0FDF4" }}>
          <svg width={28} height={28} viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 18, color: "#000" }}>
          Demande de RDV enregistrée !
        </span>
        <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1", textAlign: "center" }}>
          Confirmez votre rendez-vous pour recevoir votre lien de suivi.
        </span>
      </div>

      <div className="flex flex-col w-full rounded-xl overflow-hidden" style={{ border: "1px solid #E5E5E5" }}>
        {[
          { label: "Réf.", value: ref },
          { label: "Véhicule", value: data.immatriculation },
          { label: "Dépôt", value: `${formatDate(data.deposit_date)} ${data.deposit_time ? `vers ${data.deposit_time.split(":")[0]}h` : ""}` },
        ].map((row, i) => (
          <div key={row.label} className="flex flex-row items-center justify-between px-3 py-2.5"
            style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
            <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>{row.label}</span>
            <span style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 500, color: "#000" }}>{row.value}</span>
          </div>
        ))}
      </div>

      {!emailConfirmed ? (
        <div className="flex flex-col w-full gap-2">
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", padding: "14px 20px", background: "#25D366", color: "#fff",
                fontFamily: "Poppins", borderRadius: 10,
                textDecoration: "none", cursor: "pointer",
              }}
            >
              <svg width="22" height="22" viewBox="0 0 32 32" fill="white" style={{ flexShrink: 0 }}>
                <path fillRule="evenodd" clipRule="evenodd" d="M16 3C8.82 3 3 8.82 3 16c0 2.49.7 4.81 1.91 6.78L3 29l6.42-1.83A12.94 12.94 0 0016 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.5c-2.17 0-4.2-.62-5.93-1.7l-.42-.26-3.82 1.09 1.02-3.72-.28-.44A10.44 10.44 0 015.5 16C5.5 10.2 10.2 5.5 16 5.5S26.5 10.2 26.5 16 21.8 26.5 16 26.5zm5.95-7.8c-.33-.16-1.93-.95-2.23-1.06-.3-.11-.52-.16-.73.16-.22.33-.84 1.06-1.03 1.28-.19.22-.38.24-.71.08-.33-.16-1.38-.51-2.63-1.62-.97-.87-1.63-1.93-1.82-2.26-.19-.33-.02-.5.14-.67.15-.14.33-.38.49-.57.16-.19.22-.33.33-.55.11-.22.05-.41-.03-.57-.08-.16-.73-1.77-1-2.42-.27-.64-.54-.55-.73-.56h-.63c-.22 0-.57.08-.87.41-.3.33-1.14 1.11-1.14 2.72 0 1.6 1.17 3.15 1.33 3.37.16.22 2.3 3.51 5.57 4.92.78.34 1.39.54 1.86.69.78.25 1.49.21 2.05.13.63-.09 1.93-.79 2.2-1.55.28-.76.28-1.41.19-1.55-.08-.14-.3-.22-.63-.38z" />
              </svg>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Confirmer via WhatsApp</span>
                <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>Discutez en direct avec le garage</span>
              </div>
            </a>
          )}
          {hasEmail && (
            <button
              onClick={() => setEmailConfirmed(true)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                width: "100%", padding: "14px 20px", background: "#fff", color: "#374151",
                fontFamily: "Poppins", fontSize: 14, fontWeight: 600, borderRadius: 10,
                border: "1.5px solid #E5E5E5", cursor: "pointer",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 7l-10 7L2 7" />
              </svg>
              Confirmer par email
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 w-full">
          <div className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#16A34A" }}>
              Email de confirmation envoyé à {data.email}
            </span>
          </div>
          {trackingUrl && (
            <a
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "100%", padding: "14px 20px", background: "#5C6BFF", color: "#fff",
                fontFamily: "Poppins", fontSize: 14, fontWeight: 600, borderRadius: 10,
                textDecoration: "none", cursor: "pointer",
              }}
            >
              Suivre ma demande
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Widget V3 ──────────────────────────────────────────

export default function BookingWidgetV3({ onClose, onSuccess, internalMode, isManager }: { onClose: () => void; onSuccess?: () => void; internalMode?: boolean; isManager?: boolean }) {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const orgId = orgData?.id ?? "";
  const minDaysAhead = internalMode ? 0 : (orgData?.config?.booking_min_days_ahead ?? 1);
  const maxDaysAhead = internalMode ? 365 : (orgData?.config?.booking_max_days_ahead ?? 90);
  const srMinHoursAhead = internalMode ? 0 : (orgData?.config?.booking_sr_min_hours_ahead ?? 2);
  const safetyMarginHours = orgData?.config?.booking_safety_margin_hours ?? 1;

  const [step, setStep] = useState(0);
  const [data, setData] = useState<BookingData>({ ...INITIAL_BOOKING_DATA });
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string>("");
  const [estimatedReadyTimes, setEstimatedReadyTimes] = useState<Record<string, string>>({});
  const [trackingUrl, setTrackingUrl] = useState<string>("");
  const [whatsappPhone, setWhatsappPhone] = useState<string>("");
  const [forceSlot, setForceSlot] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    interventionsApi.list(orgId).then(setInterventions).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    if (!orgId || internalMode) return;
    sessionsApi.create(orgId).then((res) => setSessionId(res.session_id)).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    if (!sessionId || !orgId || internalMode) return;
    sessionsApi.update(orgId, sessionId, { step, plate: data.immatriculation || undefined, service: data.service || undefined }).catch(() => {});
  }, [step, sessionId]);

  const update = (key: keyof BookingData, value: unknown) => setData((prev) => ({ ...prev, [key]: value }));

  const selectedIntv = interventions.find((i) => i.name === data.intervention);
  const isFast = selectedIntv?.fast ?? false;

  const canProceed = (() => {
    if (step === 0) return data.immatriculation.replace(/[\s-]/g, "").length >= 7;
    if (step === 1) return (data.service || data.dont_know_service) && data.deposit_date && data.deposit_time;
    if (step === 2) {
      const estMin = selectedIntv?.estimated_minutes ?? 0;
      const short = estMin > 0 && estMin < SHORT_THRESHOLD;
      if (short) {
        if (data.wait_on_site === null) return false;
        if (data.wait_on_site) return !!data.restitution_date;
        return !!data.mobility_option && !!data.restitution_date && !!data.restitution_time;
      }
      if (estMin === 0) return !!data.mobility_option && !!data.restitution_date && !!data.restitution_time;
      return !!data.mobility_option && !!data.restitution_date;
    }
    if (step === 3) return data.phone.replace(/\s/g, "").length >= 10 && data.nom && data.prenom && (internalMode || data.privacy_accepted);
    return false;
  })();

  const handleNext = async () => {
    if (step < STEP_COUNT - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      setSubmitError(false);
      try {
        const result = await bookingsApi.create({
          org_id: orgId,
          plate: data.immatriculation,
          km: 0,
          service_type: SERVICES.find((s) => s.value === data.service)?.label || data.service || "Non précisé",
          intervention_type: data.intervention_type || "",
          interventions: data.intervention && data.intervention !== "__autre__" ? [data.intervention] : [],
          custom_intervention: data.custom_intervention || undefined,
          is_fast: isFast,
          mobility_option: data.mobility_option || "",
          wait_on_site: data.wait_on_site ?? false,
          tire_option: "",
          deposit_date: data.deposit_date || "",
          deposit_time: data.deposit_time,
          restitution_date: data.restitution_date || "",
          restitution_time: data.restitution_time || "",
          nom: data.nom,
          prenom: data.prenom,
          phone: data.phone.replace(/\s/g, "").replace(/^\+33/, "0"),
          email: data.email || undefined,
          communication_channel: "whatsapp",
          ...(internalMode ? { internal: true } : {}),
          ...(forceSlot ? { force: true } : {}),
        });
        setBookingId(result.id);
        setTrackingUrl(result.tracking_url || "");
        setWhatsappPhone(result.whatsapp_phone || "");
        if (sessionId) sessionsApi.update(orgId, sessionId, { status: "completed", booking_id: result.id }).catch(() => {});
        setConfirmed(true);
        onSuccess?.();
      } catch (e) {
        console.error("Error submitting booking:", e);
        setSubmitError(true);
      }
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (sessionId && !confirmed && orgId && !internalMode) sessionsApi.update(orgId, sessionId, { status: "abandoned" }).catch(() => {});
    onClose();
  };

  const containerStyle: React.CSSProperties = {
    width: 380, background: "#fff", borderRadius: 16,
    border: "1px solid #E5E5E5", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", overflow: "hidden",
  };

  if (confirmed && internalMode) {
    return (
      <div style={containerStyle}>
        <div className="flex flex-col items-center gap-5 p-5">
          <div className="flex justify-end w-full">
            <button onClick={handleClose} className="bg-transparent border-none cursor-pointer p-1">
              <Icon1 name="close" size={12} color="#A1A1A1" />
            </button>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center justify-center w-14 h-14 rounded-full" style={{ background: "#F0FDF4" }}>
              <svg width={28} height={28} viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 18, color: "#000" }}>
              RDV créé !
            </span>
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1", textAlign: "center" }}>
              {forceSlot
                ? "Le RDV a été forcé et confirmé automatiquement."
                : "Le RDV a été confirmé automatiquement."
              }
            </span>
          </div>
          <div className="flex flex-col w-full rounded-xl overflow-hidden" style={{ border: "1px solid #E5E5E5" }}>
            {[
              { label: "Réf.", value: bookingId ? `#${bookingId.slice(0, 8).toUpperCase()}` : "" },
              { label: "Client", value: `${data.prenom} ${data.nom}` },
              { label: "Véhicule", value: data.immatriculation },
              { label: "Dépôt", value: `${data.deposit_date ? data.deposit_date.split("-").reverse().join("/") : ""} à ${data.deposit_time}` },
            ].map((row, i) => (
              <div key={row.label} className="flex flex-row items-center justify-between px-3 py-2.5"
                style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>{row.label}</span>
                <span style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 500, color: "#000" }}>{row.value}</span>
              </div>
            ))}
          </div>
          <Button label="Fermer" onClick={handleClose} />
        </div>
      </div>
    );
  }

  if (confirmed) {
    return <div style={containerStyle}><ConfirmationScreen data={data} onClose={handleClose} bookingId={bookingId} trackingUrl={trackingUrl} whatsappPhone={whatsappPhone} /></div>;
  }

  if (submitError) {
    return (
      <div style={containerStyle}>
        <div className="flex flex-col items-center gap-5 p-5">
          <div className="flex justify-end w-full">
            <button onClick={handleClose} className="bg-transparent border-none cursor-pointer p-1"><Icon1 name="close" size={12} color="#A1A1A1" /></button>
          </div>
          <div className="flex flex-col items-center gap-2.5">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
              <Icon1 name="close" size={24} color="#EF4444" />
            </div>
            <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16, color: "#000" }}>Erreur</span>
            <span className="text-h4 text-neutral-400 text-center">Veuillez réessayer.</span>
          </div>
          <Button label="Réessayer" onClick={() => setSubmitError(false)} />
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div className="flex flex-col gap-4 p-5">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between">
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "#000" }}>
              {internalMode && step === 0 ? "Véhicule du client" : STEP_TITLES[step]}
            </span>
            <button onClick={handleClose} className="bg-transparent border-none cursor-pointer p-1 rounded-full">
              <Icon1 name="close" size={11} color="#A1A1A1" />
            </button>
          </div>
          <StepIndicator step={step} />
        </div>

        {/* Content */}
        {step === 0 && <StepPlate data={data} update={update} orgId={orgId} />}
        {step === 1 && <StepServiceDate data={data} update={update} orgId={orgId} minDaysAhead={minDaysAhead} maxDaysAhead={maxDaysAhead} isFast={isFast} srMinHoursAhead={srMinHoursAhead} interventions={interventions} estimatedMinutes={selectedIntv?.estimated_minutes ?? 0} onEstimatedReadyTimes={setEstimatedReadyTimes} internalMode={internalMode} isManager={isManager} onForceSlot={setForceSlot} />}
        {step === 2 && <StepRestitution data={data} update={update} estimatedMinutes={selectedIntv?.estimated_minutes ?? 0} safetyMarginHours={safetyMarginHours} depositDate={data.deposit_date} depositTime={data.deposit_time} estimatedReadyTimes={estimatedReadyTimes} />}
        {step === 3 && <StepContact data={data} update={update} orgId={orgId} internalMode={internalMode} />}

        {/* Force slot warning */}
        {forceSlot && internalMode && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
              <path d="M10 2L1 18h18L10 2z" fill="#EF4444" stroke="#DC2626" strokeWidth="1"/>
              <text x="10" y="15" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#fff">!</text>
            </svg>
            <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#991B1B" }}>
              Ce créneau est complet. Le RDV sera forcé.
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-row items-center gap-2">
          {step > 0 && <SecondaryButton label="Retour" onClick={() => setStep(step - 1)} />}
          <Button
            label={submitting ? "Envoi..." : step === STEP_COUNT - 1 ? (internalMode ? (forceSlot ? "Forcer le RDV" : "Créer le RDV") : "Confirmer") : "Suivant"}
            onClick={handleNext}
            disabled={submitting || !canProceed}
          />
        </div>
      </div>
    </div>
  );
}
